import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCanBuildWorkflows } from '../identity/useIsAdmin';
import { captureAPI, handleAPIError } from '../services/api';
import { formatDocType } from '../utils/formatDocType';
import './ExtractionInsightsPage.css';

// ── Time range helpers ───────────────────────────────────────────────
const TIME_RANGES = [
  { key: '7d',  label: 'Last 7 days',  days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time',     days: null },
];

function sinceFromKey(key) {
  const range = TIME_RANGES.find(r => r.key === key);
  if (!range || !range.days) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - range.days);
  return d.toISOString();
}

// ── Formatters ───────────────────────────────────────────────────────
const fmt$ = (v) => v != null ? `$${v.toFixed(2)}` : '$0.00';
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '0.0%';

function rateColor(rate, thresholds) {
  const [greenMax, amberMax] = thresholds;
  if (rate <= greenMax) return 'green';
  if (rate <= amberMax) return 'amber';
  return 'red';
}

// ═══════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════
const ExtractionInsightsPage = () => {
  const navigate = useNavigate();
  // 2026-06-14 B-2: "Create Template" is a builder action; members (who can
  // reach this member-open insights page) no longer see it. View / Re-extract
  // stay member-open. The canBuild gate lives in UnmatchedSection (where the
  // Create Template button renders), called there as a hook.
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [retryingIds, setRetryingIds] = useState(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = sinceFromKey(timeRange);
      const result = await captureAPI.extractionInsights.getSummary({ since });
      if (!result.ok) throw new Error(result.error || 'Failed to load insights');
      setData(result);
    } catch (err) {
      const info = handleAPIError(err, 'Extraction Insights');
      setError(info.message);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRetryExtraction = async (docId) => {
    setRetryingIds(prev => new Set(prev).add(docId));
    try {
      await captureAPI.processingMonitor.retryExtraction(docId);
    } catch {
      // non-fatal
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="ei-page">
      {/* Header */}
      <div className="ei-header">
        <div className="ei-header-left">
          <h1>Extraction Insights</h1>
          <p className="ei-subtitle">Understand AI fallback patterns and optimize templates to reduce cost.</p>
        </div>
        <div className="ei-header-right">
          {/* Time range selector */}
          <div className="ei-time-selector">
            {TIME_RANGES.map(r => (
              <button
                key={r.key}
                className={`ei-time-btn ${timeRange === r.key ? 'ei-time-btn--active' : ''}`}
                onClick={() => setTimeRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {/* Summary stats */}
          {data && !loading && (
            <div className="ei-header-stats">
              <span className="ei-header-stat">
                <strong>{data.pathways.total}</strong> docs
              </span>
              <span className="ei-header-stat">
                <strong>{fmt$(data.pathways.totalCostUsd)}</strong> AI cost
              </span>
              <span className={`ei-header-stat ei-fallback-badge ei-fallback-badge--${rateColor(data.pathways.fallbackRate, [0.2, 0.5])}`}>
                <strong>{fmtPct(data.pathways.fallbackRate)}</strong> fallback
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Loading / Error / Empty */}
      {loading && (
        <div className="ei-loading">
          <div className="ei-spinner" />
          <p>Loading extraction insights…</p>
        </div>
      )}

      {error && (
        <div className="ei-error">
          <span className="ei-error-icon">⚠️</span>
          <span>{error}</span>
          <button className="ei-action-btn" onClick={loadData}>Retry</button>
        </div>
      )}

      {!loading && !error && data && data.pathways.total === 0 && (
        <div className="ei-empty">
          <div className="ei-empty-icon">📊</div>
          <h3>No Documents Found</h3>
          <p>No documents were processed in the selected time range.</p>
        </div>
      )}

      {!loading && !error && data && data.pathways.total > 0 && (
        <>
          {/* Section 1: Pathway Distribution */}
          <PathwaySection pathways={data.pathways} />

          {/* Section 2: Template Performance */}
          <TemplateSection
            templates={data.templates}
            expandedTemplate={expandedTemplate}
            onToggleExpand={(id) => setExpandedTemplate(prev => prev === id ? null : id)}
            navigate={navigate}
          />

          {/* Section 3: Unmatched Documents */}
          <UnmatchedSection
            unmatched={data.unmatched}
            expandedGroup={expandedGroup}
            onToggleGroup={(key) => setExpandedGroup(prev => prev === key ? null : key)}
            navigate={navigate}
            retryingIds={retryingIds}
            onRetryExtraction={handleRetryExtraction}
          />

          {/* Section 4: Top Failing Fields */}
          <FailingFieldsSection
            fields={data.topFailingFields}
            navigate={navigate}
          />
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Section 1: Pathway Distribution
// ═══════════════════════════════════════════════════════════════════════
function PathwaySection({ pathways }) {
  const cards = [
    { key: 'A', color: 'green', ...pathways.A },
    { key: 'B', color: 'purple', ...pathways.B },
    { key: 'C', color: 'purple', ...pathways.C },
    { key: 'D', color: 'red', ...pathways.D },
  ];

  const total = pathways.total || 1;
  const frColor = rateColor(pathways.fallbackRate, [0.2, 0.5]);

  return (
    <div className="ei-section">
      <div className="ei-section-header">
        <h2>Pathway Distribution</h2>
        <span className="ei-badge">{pathways.total} documents</span>
      </div>

      {/* KPI strip */}
      <div className="ei-stat-cards">
        {cards.map(c => (
          <div key={c.key} className={`ei-stat-card ei-stat-card--${c.color}`}>
            <div className="ei-stat-card-label">{c.label} ({c.key})</div>
            <div className="ei-stat-card-value">{c.count}</div>
            <div className="ei-stat-card-cost">{fmt$(c.costUsd)}</div>
          </div>
        ))}
      </div>

      {/* Stacked bar */}
      <div className="ei-bar">
        {cards.map(c => {
          const pct = (c.count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={c.key}
              className={`ei-bar-segment ei-bar-segment--${c.color}`}
              style={{ width: `${pct}%` }}
              title={`${c.label}: ${c.count} (${pct.toFixed(1)}%)`}
            >
              {pct >= 8 && <span>{c.key}</span>}
            </div>
          );
        })}
      </div>

      {/* Fallback rate summary */}
      <p className={`ei-fallback-summary ei-fallback-summary--${frColor}`}>
        Fallback Rate: <strong>{fmtPct(pathways.fallbackRate)}</strong> of documents required AI processing
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Section 2: Template Performance
// ═══════════════════════════════════════════════════════════════════════
function TemplateSection({ templates, expandedTemplate, onToggleExpand, navigate }) {
  if (templates.length === 0) {
    return (
      <div className="ei-section">
        <div className="ei-section-header">
          <h2>Template Performance</h2>
        </div>
        <p className="ei-muted">No templates have been applied to documents in this time range.</p>
      </div>
    );
  }

  return (
    <div className="ei-section">
      <div className="ei-section-header">
        <h2>Template Performance</h2>
        <span className="ei-badge">{templates.length} templates</span>
      </div>

      <table className="ei-table">
        <thead>
          <tr>
            <th>Template Name</th>
            <th>Documents</th>
            <th>Avg Match Rate</th>
            <th>Fallback Rate</th>
            <th>AI Cost</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(tpl => (
            <React.Fragment key={tpl.templateId}>
              <tr
                className={`ei-row ${tpl.fallbackRate > 0.3 ? 'ei-row--danger' : tpl.fallbackRate > 0 ? 'ei-row--warning' : ''}`}
                onClick={() => onToggleExpand(tpl.templateId)}
              >
                <td className="ei-cell-name">
                  <span className="ei-expand-icon">{expandedTemplate === tpl.templateId ? '▾' : '▸'}</span>
                  <div>
                    <span className="ei-template-name">{tpl.templateName}</span>
                    {tpl.documentType && <span className="ei-template-type">{formatDocType(tpl.documentType)}</span>}
                  </div>
                </td>
                <td>{tpl.documentCount}</td>
                <td>
                  <span className={`ei-match-rate ei-match-rate--${rateColor(1 - tpl.avgMatchRate, [0.2, 0.5])}`}>
                    {fmtPct(tpl.avgMatchRate)}
                  </span>
                </td>
                <td>
                  <span className={`ei-fallback-rate ei-fallback-rate--${rateColor(tpl.fallbackRate, [0, 0.3])}`}>
                    {fmtPct(tpl.fallbackRate)}
                  </span>
                </td>
                <td>{fmt$(tpl.totalCostUsd)}</td>
                <td className="ei-cell-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="ei-action-btn"
                    onClick={() => navigate(`/template-studio?openTemplate=${tpl.templateId}`)}
                  >
                    Edit
                  </button>
                  <button
                    className="ei-action-btn ei-action-btn--secondary"
                    onClick={() => navigate(`/template-settings/${tpl.templateId}`)}
                  >
                    Settings
                  </button>
                </td>
              </tr>

              {/* Expanded field breakdown */}
              {expandedTemplate === tpl.templateId && (
                <tr className="ei-detail-row">
                  <td colSpan={6}>
                    <div className="ei-detail-content">
                      <h4>Field-Level Breakdown</h4>
                      {tpl.fields.length === 0 ? (
                        <p className="ei-muted">No extracted fields found for this template.</p>
                      ) : (
                        <table className="ei-field-table">
                          <thead>
                            <tr>
                              <th>Field Key</th>
                              <th>Extracted</th>
                              <th>Avg Confidence</th>
                              <th>Template vs AI</th>
                              <th>Failure Rate</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tpl.fields.map(f => {
                              const total = f.templateSourceCount + f.aiSourceCount;
                              const tplPct = total > 0 ? (f.templateSourceCount / total) * 100 : 0;
                              const aiPct = total > 0 ? (f.aiSourceCount / total) * 100 : 0;
                              return (
                                <tr key={f.fieldKey} className={f.failureRate > 0.3 ? 'ei-row--danger' : ''}>
                                  <td className="ei-field-key">{f.fieldKey}</td>
                                  <td>{f.extractedCount} / {tpl.documentCount}</td>
                                  <td>{fmtPct(f.avgConfidence)}</td>
                                  <td>
                                    <div className="ei-field-bar">
                                      <div className="ei-field-bar-tpl" style={{ width: `${tplPct}%` }} title={`Template: ${f.templateSourceCount}`} />
                                      <div className="ei-field-bar-ai" style={{ width: `${aiPct}%` }} title={`AI: ${f.aiSourceCount}`} />
                                    </div>
                                    <span className="ei-field-bar-label">{f.templateSourceCount}T / {f.aiSourceCount}AI</span>
                                  </td>
                                  <td>
                                    <span className={`ei-fallback-rate ei-fallback-rate--${rateColor(f.failureRate, [0, 0.3])}`}>
                                      {fmtPct(f.failureRate)}
                                    </span>
                                  </td>
                                  <td>
                                    {f.failureRate > 0.3 && (
                                      <button
                                        className="ei-action-btn ei-action-btn--warning"
                                        onClick={() => navigate(`/template-studio?openTemplate=${tpl.templateId}`)}
                                      >
                                        Adjust Zone
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Section 3: Unmatched Documents
// ═══════════════════════════════════════════════════════════════════════
function UnmatchedSection({ unmatched, expandedGroup, onToggleGroup, navigate, retryingIds, onRetryExtraction }) {
  const canBuild = useCanBuildWorkflows() === true;
  if (unmatched.totalCount === 0) {
    return (
      <div className="ei-section">
        <div className="ei-section-header">
          <h2>Unmatched Documents</h2>
        </div>
        <div className="ei-success-msg">
          <span>✅</span> All documents matched a template. No unmatched documents in this time range.
        </div>
      </div>
    );
  }

  return (
    <div className="ei-section">
      <div className="ei-section-header">
        <h2>Unmatched Documents</h2>
        <span className="ei-badge ei-badge--red">{unmatched.totalCount} docs</span>
        <span className="ei-badge ei-badge--red">{fmt$(unmatched.totalCostUsd)} AI cost</span>
      </div>
      <p className="ei-muted">These documents had no template match and were processed entirely by AI.</p>

      <div className="ei-group-list">
        {unmatched.groups.map(g => (
          <div key={g.groupKey} className="ei-group-card">
            <div className="ei-group-header" onClick={() => onToggleGroup(g.groupKey)}>
              <span className="ei-expand-icon">{expandedGroup === g.groupKey ? '▾' : '▸'}</span>
              <span className="ei-group-name">{g.groupKey}</span>
              <span className="ei-group-meta">{g.count} docs</span>
              <span className="ei-group-meta">{fmt$(g.costUsd)}</span>
            </div>

            {expandedGroup === g.groupKey && (
              <div className="ei-group-body">
                {g.sampleDocuments.map(doc => (
                  <div key={doc.id} className="ei-sample-doc">
                    <div className="ei-sample-info">
                      <span className="ei-sample-filename" title={doc.filename}>📄 {doc.filename}</span>
                      <span className="ei-sample-date">{new Date(doc.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="ei-sample-actions">
                      <button
                        className="ei-action-btn"
                        onClick={() => navigate(`/documents/${doc.id}`)}
                      >
                        View
                      </button>
                      {canBuild && (
                        <button
                          className="ei-action-btn ei-action-btn--secondary"
                          onClick={() => navigate('/template-builder', {
                            state: { documentId: doc.id, filename: doc.filename },
                          })}
                        >
                          Create Template
                        </button>
                      )}
                      <button
                        className="ei-action-btn ei-action-btn--warning"
                        disabled={retryingIds.has(doc.id)}
                        onClick={() => onRetryExtraction(doc.id)}
                      >
                        {retryingIds.has(doc.id) ? 'Re-extracting…' : 'Re-extract'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Section 4: Top Failing Fields
// ═══════════════════════════════════════════════════════════════════════
function FailingFieldsSection({ fields, navigate }) {
  if (fields.length === 0) {
    return (
      <div className="ei-section">
        <div className="ei-section-header">
          <h2>Top Failing Fields</h2>
        </div>
        <div className="ei-success-msg">
          <span>✅</span> All template fields are extracting successfully. No failures detected.
        </div>
      </div>
    );
  }

  return (
    <div className="ei-section">
      <div className="ei-section-header">
        <h2>Top Failing Fields</h2>
        <span className="ei-badge ei-badge--red">{fields.length} fields</span>
      </div>

      <table className="ei-table">
        <thead>
          <tr>
            <th>Field Key</th>
            <th>Template</th>
            <th>Failure Rate</th>
            <th>Avg Confidence</th>
            <th>Documents</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={`${f.templateId}-${f.fieldKey}-${i}`} className={f.failureRate > 0.5 ? 'ei-row--danger' : 'ei-row--warning'}>
              <td className="ei-field-key">{f.fieldKey}</td>
              <td>{f.templateName}</td>
              <td>
                <span className={`ei-fallback-rate ei-fallback-rate--${rateColor(f.failureRate, [0, 0.3])}`}>
                  {fmtPct(f.failureRate)}
                </span>
              </td>
              <td>{fmtPct(f.avgConfidence)}</td>
              <td>{f.documentCount}</td>
              <td>
                <button
                  className="ei-action-btn"
                  onClick={() => navigate(`/template-studio?openTemplate=${f.templateId}`)}
                >
                  Edit Template
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ExtractionInsightsPage;
